import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * Product classification comes from the `custom.quiz_profile` product metafield,
 * rendered into the page by main-duftquiz.liquid.
 *
 * Shape: four named groups — scent, season, occasion, audience — each mapping a
 * German category name to a [percent, color, colorDark] tuple. Percent is an
 * integer share of that group's votes; entries arrive sorted by percent
 * descending. Any group may be missing or empty if Parfumo had no votes for it.
 *
 * @typedef {[number, string, string]} ProfileEntry
 * @typedef {Record<string, Record<string, ProfileEntry>>} QuizProfile
 */

/**
 * The full question tree. Each question has an `id`, display fields,
 * an array of `options`, and a `next` (string id or null for results).
 * Individual options can override `next` for branching.
 * Each option carries a `scores` map of scent-family weights that accumulate.
 *
 * @typedef {{ value: string, label: string, description?: string, icon?: string, scores: Record<string, number>, next?: string }} QuizOption
 * @typedef {{ id: string, title: string, subtitle?: string, options: QuizOption[], next: string | null }} QuizQuestion
 */

// ═══════════════════════════════════════════════════════════════
// Quiz Styles — 3 different question sets, same scoring engine
// ═══════════════════════════════════════════════════════════════

/** @type {Record<string, {label: string, description: string, icon: string, stepOrder: string[], questions: QuizQuestion[]}>} */
const QUIZ_STYLES = {
  // ─── Style 1: Gefühlswelt (feeling-based, branching) ────────
  feeling: {
    label: 'Gefühlswelt',
    description: 'Lass dich von Stimmungen und Momenten leiten',
    icon: '✨',
    stepOrder: ['gender', 'scene', 'branch', 'feeling', 'intensity', 'occasion', 'season'],
    questions: [
      {
        id: 'gender',
        title: 'Für wen suchst du einen Duft?',
        options: [
          { value: 'Damen', label: 'Für sie', icon: '♀', scores: {} },
          { value: 'Herren', label: 'Für ihn', icon: '♂', scores: {} },
          { value: 'Egal', label: 'Egal', icon: '⚤', scores: {} },
        ],
        next: 'scene',
      },
      {
        id: 'scene',
        title: 'Schließ die Augen und stell dir vor …',
        subtitle: 'Welcher Moment weckt die stärkste Sehnsucht in dir?',
        options: [
          {
            value: 'forest',
            label: 'Ein stiller Waldweg nach dem Regen',
            description: 'Feuchtes Moos, Kiefernnadeln und frische Erde unter deinen Schuhen',
            icon: '🌲',
            scores: { Holzig: 3, Grün: 1, Erdig: 1 },
            next: 'branch_forest',
          },
          {
            value: 'garden',
            label: 'Ein blühender Garten im Morgenlicht',
            description: 'Tautropfen auf Blütenblättern, summende Bienen und warme Sonnenstrahlen',
            icon: '🌸',
            scores: { Blumig: 3, Grün: 1, Fruchtig: 1 },
            next: 'branch_garden',
          },
          {
            value: 'cozy',
            label: 'Ein gemütlicher Abend am Kaminfeuer',
            description: 'Weiche Decken, flackerndes Licht und der Duft von etwas Süßem',
            icon: '🕯️',
            scores: { Süß: 2, Gourmand: 2, Harzig: 1 },
            next: 'branch_cozy',
          },
          {
            value: 'nightout',
            label: 'Eine laue Sommernacht auf der Dachterrasse',
            description: 'Stadtlichter, leise Musik und ein Cocktail in der Hand',
            icon: '🌙',
            scores: { Orientalisch: 3, Würzig: 1, Ledrig: 1 },
            next: 'branch_nightout',
          },
          {
            value: 'beach',
            label: 'Barfuß am Meer bei Sonnenuntergang',
            description: 'Salzige Brise, warmer Sand und ein endloser Horizont',
            icon: '🏖️',
            scores: { Frisch: 2, Aquatisch: 2, Zitrus: 1 },
            next: 'branch_beach',
          },
        ],
        next: null,
      },
      {
        id: 'branch_forest',
        title: 'Du bist mitten im Wald. Was zieht dich tiefer hinein?',
        options: [
          { value: 'moss', label: 'Das weiche Moos und die kühle Erde', description: 'Der Boden federt unter deinen Schritten, alles ist still', icon: '🌿', scores: { Erdig: 2, Holzig: 1, Cremig: 1 } },
          { value: 'pine', label: 'Die klare, harzige Waldluft', description: 'Tief einatmen: Kiefern, Tanne und ein Hauch von Wind', icon: '🌬️', scores: { Frisch: 2, Grün: 1, Holzig: 1 } },
          { value: 'resin', label: 'Warmes Baumharz im Sonnenlicht', description: 'Goldene Tropfen am Stamm, süßlich-warm und geheimnisvoll', icon: '🍯', scores: { Harzig: 2, Orientalisch: 1, Holzig: 1 } },
        ],
        next: 'feeling',
      },
      {
        id: 'branch_garden',
        title: 'Du schlenderst durch den Garten. Was zieht dich an?',
        options: [
          { value: 'roses', label: 'Der samtige Duft von Rosen und Pfingstrosen', description: 'Klassisch, romantisch, wie ein altenglischer Garten', icon: '🌹', scores: { Blumig: 2, Pudrig: 1, Süß: 1 } },
          { value: 'wildflowers', label: 'Wilde Blumen und frische Kräuter am Wegrand', description: 'Unbeschwert und lebendig, wie eine Sommerwiese', icon: '🌼', scores: { Grün: 2, Blumig: 1, Frisch: 1 } },
          { value: 'exotic', label: 'Betörende Jasminblüten in der Abenddämmerung', description: 'Schwer, sinnlich und fast schon berauschend', icon: '🪷', scores: { Orientalisch: 1, Animalisch: 1, Blumig: 1, Süß: 1 } },
        ],
        next: 'feeling',
      },
      {
        id: 'branch_cozy',
        title: 'Der perfekte gemütliche Abend: was macht ihn komplett?',
        options: [
          { value: 'vanilla', label: 'Vanilleduft und frisch gebackene Kekse', description: 'Süß, warm und tröstend wie Kindheitserinnerungen', icon: '🍪', scores: { Gourmand: 2, Süß: 1, Pudrig: 1 } },
          { value: 'chai', label: 'Eine Tasse Chai mit Zimt und Kardamom', description: 'Würzig-warm, ein Buch auf dem Schoß, Regen am Fenster', icon: '☕', scores: { Würzig: 2, Harzig: 1, Süß: 1 } },
          { value: 'cashmere', label: 'Weicher Kaschmir und ein Glas Rotwein', description: 'Sanft, elegant und unbeschwert, purer Luxus', icon: '🍷', scores: { Cremig: 2, Holzig: 1, Pudrig: 1 } },
        ],
        next: 'feeling',
      },
      {
        id: 'branch_nightout',
        title: 'Die Nacht ist noch jung. Welche Stimmung zieht dich an?',
        options: [
          { value: 'electric', label: 'Aufregend und voller Energie', description: 'Pulsierende Beats, neue Gesichter, alles ist möglich', icon: '⚡', scores: { Würzig: 2, Synthetisch: 1, Frisch: 1 } },
          { value: 'romantic', label: 'Sinnlich und romantisch', description: 'Leise Gespräche, Kerzenlicht und Nähe', icon: '💋', scores: { Süß: 2, Orientalisch: 1, Pudrig: 1 } },
          { value: 'sophisticated', label: 'Cool und selbstbewusst', description: 'Du lehnst lässig an der Bar und genießt den Moment', icon: '🖤', scores: { Ledrig: 1, Rauchig: 1, Würzig: 1, Holzig: 1 } },
        ],
        next: 'feeling',
      },
      {
        id: 'branch_beach',
        title: 'Die Sonne geht unter am Meer. Was genießt du am meisten?',
        options: [
          { value: 'breeze', label: 'Die kühle Meeresbrise auf der Haut', description: 'Salz, Weite und das Rauschen der Wellen', icon: '🌊', scores: { Aquatisch: 2, Frisch: 1, Cremig: 1 } },
          { value: 'sand', label: 'Warmen Sand und den Duft von Kokosöl', description: 'Sonne auf der Haut, Leichtigkeit und Urlaubsgefühl', icon: '🥥', scores: { Cremig: 2, Synthetisch: 1, Süß: 1 } },
          { value: 'tropical', label: 'Tropische Früchte und goldenes Licht', description: 'Mango, Passionsfrucht und die Farben des Sonnenuntergangs', icon: '🍍', scores: { Fruchtig: 2, Süß: 1, Zitrus: 1 } },
        ],
        next: 'feeling',
      },
      {
        id: 'feeling',
        title: 'Wie soll sich der Duft auf deiner Haut anfühlen?',
        subtitle: 'Welches Wort trifft es am besten?',
        options: [
          { value: 'elegant', label: 'Elegant & Zeitlos', description: 'Wie ein perfekt geschnittenes Kleidungsstück, raffiniert und souverän', icon: '🖤', scores: { Chypre: 2, Pudrig: 1, Holzig: 1 } },
          { value: 'cozy', label: 'Warm & Geborgen', description: 'Wie eine Umarmung von jemandem, den du liebst', icon: '🤎', scores: { Cremig: 2, Gourmand: 1, Süß: 1 } },
          { value: 'fresh', label: 'Frisch & Belebend', description: 'Wie der erste Atemzug nach einem Sommerregen', icon: '💚', scores: { Frisch: 1, Zitrus: 1, Grün: 1, Aquatisch: 1 } },
          { value: 'mysterious', label: 'Geheimnisvoll & Verführerisch', description: 'Ein Duft, der neugierig macht und in Erinnerung bleibt', icon: '💜', scores: { Orientalisch: 1, Rauchig: 1, Animalisch: 1, Ledrig: 1 } },
          { value: 'playful', label: 'Verspielt & Lebendig', description: 'Wie ein spontanes Lachen, fröhlich, leicht und ansteckend', icon: '🧡', scores: { Fruchtig: 1, Süß: 1, Synthetisch: 1, Zitrus: 1 } },
        ],
        next: 'intensity',
      },
      {
        id: 'intensity',
        title: 'Wie präsent soll dein Duft sein?',
        options: [
          { value: 'subtle', label: 'Dezent & Nah', description: 'Nur wer dich umarmt, riecht ihn', icon: '🤫', scores: {} },
          { value: 'moderate', label: 'Angenehme Duftspur', description: 'Man riecht dich kurz, wenn du vorbeigehst', icon: '〰️', scores: {} },
          { value: 'bold', label: 'Kraftvoll & Auffällig', description: 'Alle im Raum sollen ihn bemerken', icon: '💥', scores: {} },
        ],
        next: 'occasion',
      },
      {
        id: 'occasion',
        title: 'Wann wirst du den Duft am meisten tragen?',
        options: [
          { value: 'work', label: 'Morgens auf dem Weg zur Arbeit', description: 'Professionell, frisch und nicht aufdringlich', icon: '💼', scores: {} },
          { value: 'leisure', label: 'Am Wochenende in meiner Freizeit', description: 'Entspannt, lässig und gute Laune', icon: '🎧', scores: {} },
          { value: 'evening', label: 'Abends beim Ausgehen oder auf Events', description: 'Besondere Anlässe, Date Night, Feiern', icon: '🥂', scores: {} },
          { value: 'daily', label: 'Jeden Tag, als mein Signature-Duft', description: 'Der Duft, der zu mir gehört, immer und überall', icon: '⭐', scores: {} },
        ],
        next: 'season',
      },
      {
        id: 'season',
        title: 'Zu welcher Jahreszeit fühlst du dich am wohlsten?',
        options: [
          { value: 'Frühling', label: 'Frühling', description: 'Alles blüht, die Luft ist frisch und voller Hoffnung', icon: '🌸', scores: {} },
          { value: 'Sommer', label: 'Sommer', description: 'Warme Tage, lange Abende und Leichtigkeit', icon: '☀️', scores: {} },
          { value: 'Herbst', label: 'Herbst', description: 'Bunte Blätter, kuschelige Pullover und goldenes Licht', icon: '🍂', scores: {} },
          { value: 'Winter', label: 'Winter', description: 'Kalte Luft, warme Getränke und Gemütlichkeit', icon: '❄️', scores: {} },
        ],
        next: null,
      },
    ],
  },

  // ─── Style 2: Schnell & Direkt (quick, no branching) ────────
  quick: {
    label: 'Schnell & Direkt',
    description: 'Du weißt was du magst, 5 Fragen, fertig',
    icon: '⚡',
    stepOrder: ['gender', 'scent_direct', 'intensity', 'occasion', 'season'],
    questions: [
      {
        id: 'gender',
        title: 'Für wen suchst du?',
        options: [
          { value: 'Damen', label: 'Für sie', icon: '♀', scores: {} },
          { value: 'Herren', label: 'Für ihn', icon: '♂', scores: {} },
          { value: 'Egal', label: 'Egal', icon: '⚤', scores: {} },
        ],
        next: 'scent_direct',
      },
      {
        id: 'scent_direct',
        title: 'Welche Duftrichtung magst du?',
        options: [
          { value: 'fresh', label: 'Frisch & Klar', description: 'Zitrus, Meeresbrise, grüne Noten', icon: '🍋', scores: { Frisch: 4, Zitrus: 3, Aquatisch: 2, Grün: 1 } },
          { value: 'floral', label: 'Blumig & Zart', description: 'Rose, Jasmin, Iris', icon: '🌸', scores: { Blumig: 4, Pudrig: 2, Fruchtig: 2, Chypre: 1 } },
          { value: 'woody', label: 'Holzig & Erdig', description: 'Sandelholz, Zeder, Vetiver', icon: '🌲', scores: { Holzig: 4, Erdig: 2, Harzig: 2, Chypre: 1 } },
          { value: 'oriental', label: 'Orientalisch & Warm', description: 'Amber, Gewürze, Weihrauch', icon: '🕌', scores: { Orientalisch: 4, Würzig: 2, Animalisch: 1, Harzig: 1 } },
          { value: 'sweet', label: 'Süß & Gourmand', description: 'Karamell, Vanille, Tonkabohne', icon: '🍯', scores: { Süß: 4, Gourmand: 3, Cremig: 2, Pudrig: 1 } },
          { value: 'spicy', label: 'Würzig & Rauchig', description: 'Leder, Pfeffer, Tabak', icon: '🔥', scores: { Würzig: 4, Ledrig: 2, Rauchig: 2, Animalisch: 1, Synthetisch: 1 } },
        ],
        next: 'intensity',
      },
      {
        id: 'intensity',
        title: 'Wie stark soll der Duft sein?',
        options: [
          { value: 'subtle', label: 'Dezent', icon: '🤫', scores: {} },
          { value: 'moderate', label: 'Mittlere Spur', icon: '〰️', scores: {} },
          { value: 'bold', label: 'Kraftvoll', icon: '💥', scores: {} },
        ],
        next: 'occasion',
      },
      {
        id: 'occasion',
        title: 'Wofür ist der Duft gedacht?',
        options: [
          { value: 'work', label: 'Arbeit & Alltag', icon: '💼', scores: {} },
          { value: 'leisure', label: 'Freizeit & Wochenende', icon: '🎧', scores: {} },
          { value: 'evening', label: 'Ausgehen & Events', icon: '🥂', scores: {} },
          { value: 'daily', label: 'Überall, Signature', icon: '⭐', scores: {} },
        ],
        next: 'season',
      },
      {
        id: 'season',
        title: 'Für welche Jahreszeit?',
        options: [
          { value: 'Frühling', label: 'Frühling', icon: '🌸', scores: {} },
          { value: 'Sommer', label: 'Sommer', icon: '☀️', scores: {} },
          { value: 'Herbst', label: 'Herbst', icon: '🍂', scores: {} },
          { value: 'Winter', label: 'Winter', icon: '❄️', scores: {} },
        ],
        next: null,
      },
    ],
  },

  // ─── Style 3: Lifestyle (personality-based) ─────────────────
  lifestyle: {
    label: 'Lifestyle',
    description: 'Erzähl uns von dir, wir finden den Rest',
    icon: '🎭',
    stepOrder: ['gender', 'personality', 'weekend', 'wardrobe', 'vibe', 'occasion', 'intensity', 'season'],
    questions: [
      {
        id: 'gender',
        title: 'Für wen suchst du einen Duft?',
        options: [
          { value: 'Damen', label: 'Für sie', icon: '♀', scores: {} },
          { value: 'Herren', label: 'Für ihn', icon: '♂', scores: {} },
          { value: 'Egal', label: 'Egal', icon: '⚤', scores: {} },
        ],
        next: 'personality',
      },
      {
        id: 'personality',
        title: 'Wie würden dich deine Freunde beschreiben?',
        subtitle: 'Wähle die Beschreibung, die am besten passt.',
        options: [
          { value: 'creative', label: 'Kreativ & Träumerisch', description: 'Immer mit neuen Ideen, ein bisschen in der eigenen Welt', icon: '🎨', scores: { Blumig: 2, Orientalisch: 1, Fruchtig: 1, Pudrig: 1 } },
          { value: 'adventurous', label: 'Abenteuerlustig & Spontan', description: 'Immer unterwegs, liebt das Neue und Unbekannte', icon: '🧭', scores: { Frisch: 2, Würzig: 1, Zitrus: 1, Aquatisch: 1 } },
          { value: 'elegant', label: 'Stilvoll & Kultiviert', description: 'Guter Geschmack, Liebe zum Detail', icon: '🖤', scores: { Holzig: 2, Chypre: 1, Pudrig: 1, Ledrig: 1 } },
          { value: 'warmhearted', label: 'Warmherzig & Gemütlich', description: 'Die Person, bei der sich alle willkommen fühlen', icon: '🤎', scores: { Süß: 2, Gourmand: 1, Cremig: 1, Harzig: 1 } },
          { value: 'thoughtful', label: 'Ruhig & Nachdenklich', description: 'Tiefgründig, beobachtet gern und genießt die Stille', icon: '📖', scores: { Holzig: 1, Erdig: 2, Rauchig: 1, Harzig: 1 } },
        ],
        next: 'weekend',
      },
      {
        id: 'weekend',
        title: 'Dein perfektes Wochenende?',
        options: [
          { value: 'nature', label: 'Wandern, Natur, frische Luft', description: 'Berge, Wälder oder einfach raus ins Grüne', icon: '🏔️', scores: { Holzig: 2, Grün: 2, Erdig: 1 } },
          { value: 'city', label: 'Café, Shopping, Stadt erkunden', description: 'Neue Orte entdecken und das Stadtleben genießen', icon: '🏙️', scores: { Blumig: 1, Frisch: 1, Zitrus: 1, Synthetisch: 1, Fruchtig: 1 } },
          { value: 'cozy', label: 'Zuhause, Kochen, Netflix', description: 'Gemütlichkeit pur mit gutem Essen und Entspannung', icon: '🛋️', scores: { Gourmand: 2, Süß: 1, Cremig: 1, Harzig: 1 } },
          { value: 'nightlife', label: 'Party, Bars, Freunde treffen', description: 'Ausgehen, tanzen und die Nacht genießen', icon: '🍸', scores: { Orientalisch: 2, Würzig: 1, Ledrig: 1, Animalisch: 1 } },
          { value: 'beach', label: 'Am Wasser, Strand, Pool', description: 'Sonne, Meer und absolute Entspannung', icon: '🏖️', scores: { Aquatisch: 2, Frisch: 1, Zitrus: 1, Cremig: 1 } },
        ],
        next: 'wardrobe',
      },
      {
        id: 'wardrobe',
        title: 'Dein Kleiderschrank in einem Wort?',
        options: [
          { value: 'minimal', label: 'Minimalistisch', description: 'Schwarz, Weiß, klare Linien', icon: '⬛', scores: { Holzig: 1, Frisch: 1, Cremig: 1, Chypre: 1, Synthetisch: 1 } },
          { value: 'romantic', label: 'Romantisch', description: 'Fließende Stoffe, weiche Farben', icon: '🩷', scores: { Blumig: 2, Süß: 1, Pudrig: 1, Fruchtig: 1 } },
          { value: 'urban', label: 'Urban & Modern', description: 'Lässig, zeitgemäß, eigener Style', icon: '🧢', scores: { Frisch: 1, Würzig: 1, Zitrus: 1, Synthetisch: 1, Aquatisch: 1 } },
          { value: 'classic', label: 'Klassisch-Elegant', description: 'Zeitlose Stücke, gut kombiniert', icon: '👔', scores: { Orientalisch: 1, Holzig: 1, Ledrig: 1, Pudrig: 1, Chypre: 1 } },
          { value: 'bohemian', label: 'Naturverbunden & Boho', description: 'Erdtöne, natürliche Materialien, entspannt', icon: '🌿', scores: { Grün: 1, Erdig: 1, Holzig: 1, Harzig: 1, Animalisch: 1 } },
        ],
        next: 'vibe',
      },
      {
        id: 'vibe',
        title: 'Welche Musik passt zu deiner Stimmung?',
        subtitle: 'Stell dir vor, du hörst deinen Lieblingssong: welches Genre kommt dem am nächsten?',
        options: [
          { value: 'pop', label: 'Pop & Mainstream', description: 'Eingängig, fröhlich, gute Laune', icon: '🎤', scores: { Fruchtig: 2, Süß: 1, Blumig: 1, Frisch: 1 } },
          { value: 'electronic', label: 'Elektronisch & Modern', description: 'Beats, Synthie-Sounds, futuristisch', icon: '🎧', scores: { Synthetisch: 2, Aquatisch: 1, Frisch: 1, Zitrus: 1 } },
          { value: 'rock', label: 'Rock & Alternative', description: 'Rau, intensiv, rebellisch', icon: '🎸', scores: { Ledrig: 1, Rauchig: 2, Würzig: 1, Animalisch: 1 } },
          { value: 'jazz', label: 'Jazz, Soul & R&B', description: 'Smooth, sinnlich, voller Tiefe', icon: '🎷', scores: { Orientalisch: 1, Cremig: 1, Pudrig: 1, Animalisch: 1, Chypre: 1 } },
          { value: 'classical', label: 'Klassik & Ambient', description: 'Ruhig, elegant, zeitlos', icon: '🎻', scores: { Holzig: 1, Blumig: 1, Grün: 1, Erdig: 1, Harzig: 1 } },
        ],
        next: 'occasion',
      },
      {
        id: 'occasion',
        title: 'Wann brauchst du deinen Duft am meisten?',
        subtitle: 'In welcher Situation ist er dir am wichtigsten?',
        options: [
          { value: 'work', label: 'Morgens auf dem Weg zur Arbeit', description: 'Ein frischer Start in den Tag', icon: '☕', scores: {} },
          { value: 'leisure', label: 'Am Wochenende in meiner Freizeit', description: 'Entspannt und unbeschwert', icon: '🎧', scores: {} },
          { value: 'evening', label: 'Abends beim Ausgehen oder auf Events', description: 'Besondere Anlässe, Date Night, Feiern', icon: '🥂', scores: {} },
          { value: 'daily', label: 'Jeden Tag, als mein Signature-Duft', description: 'Der Duft, der zu mir gehört, immer und überall', icon: '⭐', scores: {} },
        ],
        next: 'intensity',
      },
      {
        id: 'intensity',
        title: 'Wie präsent soll dein Duft sein?',
        options: [
          { value: 'subtle', label: 'Dezent & Nah', description: 'Nur wer dich umarmt, riecht ihn', icon: '🤫', scores: {} },
          { value: 'moderate', label: 'Angenehme Duftspur', description: 'Man riecht dich kurz, wenn du vorbeigehst', icon: '〰️', scores: {} },
          { value: 'bold', label: 'Kraftvoll & Auffällig', description: 'Alle im Raum sollen ihn bemerken', icon: '💥', scores: {} },
        ],
        next: 'season',
      },
      {
        id: 'season',
        title: 'Zu welcher Jahreszeit fühlst du dich am wohlsten?',
        options: [
          { value: 'Frühling', label: 'Frühling', description: 'Alles blüht, die Luft ist frisch und voller Hoffnung', icon: '🌸', scores: {} },
          { value: 'Sommer', label: 'Sommer', description: 'Warme Tage, lange Abende und Leichtigkeit', icon: '☀️', scores: {} },
          { value: 'Herbst', label: 'Herbst', description: 'Bunte Blätter, kuschelige Pullover und goldenes Licht', icon: '🍂', scores: {} },
          { value: 'Winter', label: 'Winter', description: 'Kalte Luft, warme Getränke und Gemütlichkeit', icon: '❄️', scores: {} },
        ],
        next: null,
      },
    ],
  },
};

/** Map occasion answer values to `occasion` category names */
const OCCASION_MAP = {
  work: ['Arbeit', 'Täglich'],
  leisure: ['Freizeit'],
  evening: ['Ausgehen', 'Abend'],
  daily: ['Täglich'],
};

/** Map intensity answer values to sillage ranges (0-100, see sillageOutOf100) */
const INTENSITY_RANGES = {
  subtle: [0, 40],
  moderate: [30, 70],
  bold: [60, 100],
};

/** Rank labels for the top 3 results (used in result card header) */
const RANK_LABELS = [
  { label: 'Dein Top-Match' },
  { label: 'Starkes Match' },
  { label: 'Geheimtipp' },
];

/** Display phrasing for occasion category names (German articles/prepositions) */
const OCCASION_DISPLAY = {
  Arbeit: 'zur Arbeit',
  Täglich: 'in den Alltag',
  Freizeit: 'in die Freizeit',
  Ausgehen: 'zum Ausgehen',
  Abend: 'für den Abend',
};

/**
 * The product's dominant scent family. Entries are pre-sorted by percent, so the
 * first one wins.
 * @param {QuizProfile} profile
 * @returns {string | undefined}
 */
function topScentFamily(profile) {
  return groupEntries(profile, 'scent')[0]?.[0];
}

/**
 * A category's share of its group, or 0 when the group or category is absent.
 * @param {QuizProfile} profile
 * @param {string} group
 * @param {string} name exact German category name
 * @returns {number} percent 0-100
 */
function percentOf(profile, group, name) {
  const entry = profile?.[group]?.[name];
  return Array.isArray(entry) ? entry[0] : 0;
}

/**
 * A group's categories, still in the metafield's percent-descending order.
 * @param {QuizProfile} profile
 * @param {string} group
 * @returns {Array<[string, ProfileEntry]>}
 */
function groupEntries(profile, group) {
  const g = profile?.[group];
  return g ? Object.entries(g) : [];
}

/**
 * `custom.rating_sillage` is stored on Parfumo's 0-10 scale, but the intensity
 * bands and bar widths below are 0-100. Ratings are omitted entirely for
 * barely-voted perfumes, so absence is meaningful and must not become 0.
 * @param {{ sillage?: number|null }} product
 * @returns {number|null} sillage 0-100, or null when unrated
 */
function sillageOutOf100(product) {
  if (product?.sillage == null) return null;
  const raw = Number(product.sillage);
  return Number.isNaN(raw) ? null : raw * 10;
}

/**
 * @param {number} value sillage 0-100
 * @returns {string}
 */
function getSillageLabel(value) {
  if (value < 30) return 'Dezent';
  if (value < 55) return 'Mittlere Spur';
  if (value < 75) return 'Spürbar';
  return 'Kraftvoll';
}

/**
 * Build a lookup map from a questions array, keyed by question id.
 * @param {QuizQuestion[]} questions
 * @returns {Map<string, QuizQuestion>}
 */
function buildQuestionMap(questions) {
  const map = new Map();
  for (const q of questions) {
    map.set(q.id, q);
  }
  return map;
}

// ─── Component ─────────────────────────────────────────────────

class DuftquizComponent extends Component {
  requiredRefs = ['introScreen', 'quizScreen', 'resultsScreen', 'resultsGrid', 'atcBtn', 'stepContainer'];

  /** @type {string|null} Selected quiz style key */
  #activeStyle = null;

  /** @type {Map<string, QuizQuestion>} */
  #questionMap = new Map();

  /** @type {string[]} Step order for progress display */
  #stepOrder = [];

  /** @type {Array<{questionId: string, value: string}>} */
  #history = [];

  /** @type {string} */
  #currentQuestionId = '';

  /** @type {Record<string, string>} */
  #answers = {};

  /** @type {Record<string, number>} */
  #scentProfile = {};

  /** @type {Array<{id: number, title: string, handle: string, url: string, vendor: string, probeVariantId: number, price: number, image: string, profile: QuizProfile, sillage: number|null}>} */
  #products = [];

  /** @type {Array<{product: any, score: number}>} */
  #results = [];

  /** @type {number} Timestamp when the active quiz run started */
  #startedAt = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#loadProducts();
  }

  /**
   * Push a structured event to GTM's dataLayer. No-op if dataLayer isn't loaded.
   * @param {string} event
   * @param {Record<string, any>} [payload]
   */
  #track(event, payload = {}) {
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') return;
    window.dataLayer.push({ event, quiz_name: 'duftquiz', ...payload });
  }

  #loadProducts() {
    const el = document.getElementById('duftquiz-products');
    if (!el) return;
    try {
      const data = JSON.parse(el.textContent || '[]');
      this.#products = data.filter((p) => p && p.probeVariantId && p.profile);
    } catch (e) {
      console.error('Failed to parse quiz product data:', e);
    }
  }

  // ── Public methods (called via on:click) ───────────────────

  /**
   * Called when user picks a quiz style from the intro screen.
   * @param {{ style: string }} data
   */
  selectStyle(data) {
    const style = QUIZ_STYLES[data.style];
    if (!style || !style.questions.length) return;

    // No scoreable products means every result would be empty — say so rather
    // than walk the user through the questions for nothing.
    if (!this.#products.length) {
      this.#showScreen('loading');
      const loadingText = this.querySelector('.duftquiz__loading-text');
      if (loadingText) loadingText.textContent = 'Quiz konnte nicht geladen werden...';
      return;
    }

    this.#activeStyle = data.style;
    this.#questionMap = buildQuestionMap(style.questions);
    this.#stepOrder = style.stepOrder;

    this.#history = [];
    this.#answers = {};
    this.#scentProfile = {};
    this.#startedAt = Date.now();
    this.#currentQuestionId = style.questions[0].id;
    this.#showScreen('quiz');
    this.#renderQuestion(this.#currentQuestionId);

    this.#track('duftquiz_start', {
      quiz_style: this.#activeStyle,
      total_steps: this.#stepOrder.length,
    });
  }

  startQuiz() {
    // Legacy: if called without style selection, default to feeling
    this.selectStyle({ style: 'feeling' });
  }

  /**
   * Called by option buttons via on:click.
   * @param {{ qid: string, value: string }} data
   */
  pickAnswer(data) {
    const question = this.#questionMap.get(data.qid);
    if (!question) return;

    const option = question.options.find((o) => o.value === data.value);
    if (!option) return;

    this.#answers[data.qid] = data.value;
    this.#history.push({ questionId: data.qid, value: data.value });

    if (option.scores) {
      for (const [family, weight] of Object.entries(option.scores)) {
        this.#scentProfile[family] = (this.#scentProfile[family] || 0) + weight;
      }
    }

    const { step, total } = this.#getStepInfo(data.qid);
    this.#track('duftquiz_answer', {
      quiz_style: this.#activeStyle,
      question_id: data.qid,
      answer_value: data.value,
      answer_label: option.label,
      step,
      total_steps: total,
    });

    const container = this.refs.stepContainer;
    container.querySelectorAll('.duftquiz__option').forEach((el) => {
      el.classList.toggle('duftquiz__option--selected', el.dataset.value === data.value);
    });

    const nextId = option.next ?? question.next;

    setTimeout(() => {
      if (nextId) {
        this.#currentQuestionId = nextId;
        this.#renderQuestion(nextId);
      } else {
        this.#calculateResults();
      }
    }, 300);
  }

  goBack() {
    if (this.#history.length === 0) {
      // Go back to style selection
      this.#showScreen('intro');
      return;
    }

    const last = this.#history.pop();
    if (!last) return;

    const question = this.#questionMap.get(last.questionId);
    const option = question?.options.find((o) => o.value === last.value);
    if (option?.scores) {
      for (const [family, weight] of Object.entries(option.scores)) {
        this.#scentProfile[family] = (this.#scentProfile[family] || 0) - weight;
      }
    }

    delete this.#answers[last.questionId];

    if (this.#history.length === 0) {
      const style = QUIZ_STYLES[this.#activeStyle];
      this.#currentQuestionId = style.questions[0].id;
    } else {
      this.#currentQuestionId = last.questionId;
    }

    this.#renderQuestion(this.#currentQuestionId);
  }

  async addAllToCart() {
    const { atcBtn, atcStatus } = this.refs;
    if (!this.#results.length) return;

    atcBtn.disabled = true;
    atcStatus.className = 'duftquiz__atc-status duftquiz__atc-status--hidden';

    const bundleId = `dq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const items = this.#results.map(({ product }) => ({
      id: product.probeVariantId,
      quantity: 1,
      properties: {
        _from_quiz: 'true',
        _bundle_item: 'duftquiz',
        _bundle_id: bundleId,
      },
    }));

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      const data = await res.json();

      if (data.status) {
        throw new Error(data.message || 'Error adding to cart');
      }

      this.dispatchEvent(
        new CartAddEvent({}, 'duftquiz', {
          source: 'duftquiz-component',
          itemCount: items.length,
        })
      );

      this.#track('duftquiz_addtocart', {
        quiz_style: this.#activeStyle,
        bundle_id: bundleId,
        item_count: items.length,
        product_ids: this.#results.map((r) => r.product.id),
        variant_ids: items.map((i) => i.id),
      });

      atcStatus.textContent = 'Erfolgreich hinzugefügt! Rabatt wird im Warenkorb angezeigt.';
      atcStatus.className = 'duftquiz__atc-status duftquiz__atc-status--success';
    } catch (e) {
      console.error('Failed to add quiz results to cart:', e);
      atcStatus.textContent = e.message || 'Fehler beim Hinzufügen';
      atcStatus.className = 'duftquiz__atc-status duftquiz__atc-status--error';
    } finally {
      atcBtn.disabled = false;
    }
  }

  restartQuiz() {
    this.#history = [];
    this.#answers = {};
    this.#scentProfile = {};
    this.#results = [];
    this.#activeStyle = null;
    this.#showScreen('intro');
  }

  // ── Private rendering ──────────────────────────────────────

  #getStepInfo(questionId) {
    const order = this.#stepOrder;
    const total = order.length;

    let idx;
    if (questionId.startsWith('branch_')) {
      idx = order.indexOf('branch');
    } else {
      idx = order.indexOf(questionId);
    }

    return { step: idx >= 0 ? idx + 1 : 1, total };
  }

  #renderQuestion(questionId) {
    const question = this.#questionMap.get(questionId);
    if (!question) return;

    const { stepContainer, progressFill, stepCounter } = this.refs;
    const { step, total } = this.#getStepInfo(questionId);

    progressFill.style.width = `${(step / total) * 100}%`;
    stepCounter.textContent = `Frage ${step} von ${total}`;

    const showBack = true; // always show — goes to style picker when history is empty
    const optionsHtml = question.options
      .map(
        (opt) => `
      <button
        class="duftquiz__option"
        on:click="duftquiz-component/pickAnswer?qid=${question.id}&value=${encodeURIComponent(opt.value)}"
        data-value="${opt.value}"
      >
        ${opt.icon ? `<span class="duftquiz__option-icon">${opt.icon}</span>` : ''}
        <span class="duftquiz__option-text">
          <span class="duftquiz__option-label">${opt.label}</span>
          ${opt.description ? `<span class="duftquiz__option-desc">${opt.description}</span>` : ''}
        </span>
      </button>`
      )
      .join('');

    stepContainer.innerHTML = `
      <div class="duftquiz__step" key="${questionId}">
        ${showBack ? `<button class="duftquiz__back-btn" on:click="duftquiz-component/goBack" aria-label="Zurück"><svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Zurück</button>` : ''}
        <h2 class="duftquiz__step-title">${question.title}</h2>
        ${question.subtitle ? `<p class="duftquiz__step-subtitle">${question.subtitle}</p>` : ''}
        <div class="duftquiz__options">${optionsHtml}</div>
      </div>
    `;
  }

  // ── Scoring ────────────────────────────────────────────────

  #calculateResults() {
    this.#showScreen('loading');

    const scored = this.#products
      .map((product) => ({ product, score: this.#scoreProduct(product) }))
      .sort((a, b) => b.score - a.score);

    // Greedy pick with mild diversity nudge — still stays in the same ballpark
    /** @type {typeof scored} */
    const picked = [];
    const candidates = [...scored];

    for (let i = 0; i < 3 && candidates.length > 0; i++) {
      if (i === 0) {
        const first = candidates.shift();
        if (first) picked.push(first);
        continue;
      }

      // Get top scent families from already-picked products
      const pickedFamilies = new Set();
      for (const p of picked) {
        const top = topScentFamily(p.product.profile);
        if (top) pickedFamilies.add(top);
      }

      // Apply a small penalty if the candidate's top family already appears in picked
      let bestIdx = 0;
      let bestAdjusted = -Infinity;

      for (let j = 0; j < Math.min(candidates.length, 10); j++) {
        const candidate = candidates[j];
        if (!candidate) continue;
        let adjusted = candidate.score;

        const top = topScentFamily(candidate.product.profile);
        if (top && pickedFamilies.has(top)) {
          adjusted *= 0.85; // 15% nudge down — enough to prefer variety, not enough to pick a bad match
        }

        if (adjusted > bestAdjusted) {
          bestAdjusted = adjusted;
          bestIdx = j;
        }
      }

      const [chosen] = candidates.splice(bestIdx, 1);
      if (chosen) picked.push(chosen);
    }

    this.#results = picked;

    const topFamilies = Object.entries(this.#scentProfile)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([f]) => f);

    this.#track('duftquiz_complete', {
      quiz_style: this.#activeStyle,
      duration_ms: this.#startedAt ? Date.now() - this.#startedAt : null,
      answers: { ...this.#answers },
      top_scent_families: topFamilies,
      result_product_ids: picked.map((r) => r.product.id),
      result_product_titles: picked.map((r) => r.product.title),
    });

    setTimeout(() => {
      this.#renderResults(picked);
      this.#showScreen('results');
    }, 800);
  }

  /**
   * @param {{ profile: QuizProfile, sillage: number|null }} product
   * @returns {number}
   */
  #scoreProduct(product) {
    const { profile } = product;
    let score = 0;

    for (const [family, weight] of Object.entries(this.#scentProfile)) {
      score += weight * percentOf(profile, 'scent', family);
    }

    // The `gender` question asks who the fragrance is for, which the metafield
    // answers with its `audience` group (Herren/Damen/Klassisch/Modern) — that's
    // who Parfumo's voters say wears it, not the product's own gender field.
    const gender = this.#answers.gender;
    if (gender && gender !== 'Egal') {
      score += percentOf(profile, 'audience', gender) * 1.5;
    }

    const occasionAnswer = this.#answers.occasion;
    if (occasionAnswer) {
      for (const name of OCCASION_MAP[occasionAnswer] || []) {
        score += percentOf(profile, 'occasion', name);
      }
    }

    const season = this.#answers.season;
    if (season) {
      score += percentOf(profile, 'season', season) * 1.2;
    }

    const intensity = this.#answers.intensity;
    const sillage = sillageOutOf100(product);
    if (intensity && sillage !== null) {
      const [lo, hi] = INTENSITY_RANGES[intensity] || [0, 100];
      if (sillage >= lo && sillage <= hi) {
        score += 15;
      }
    }

    return score;
  }

  /**
   * Build short "why it matches" bullet points based on quiz answers vs product data.
   * @param {QuizProfile} profile
   * @returns {string[]} max 3 HTML-safe phrases (may contain <strong>)
   */
  #getMatchHighlights(profile) {
    const out = [];

    // 1. Strongest user-preferred scent family that this product actually has
    const userTopFamilies = Object.entries(this.#scentProfile)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([f]) => f);

    for (const family of userTopFamilies) {
      if (percentOf(profile, 'scent', family) > 25) {
        out.push(`<strong>${family}</strong>, wie du es wolltest`);
        break;
      }
    }

    // 2. Occasion match
    const userOccasion = this.#answers.occasion;
    if (userOccasion) {
      for (const name of OCCASION_MAP[userOccasion] || []) {
        if (percentOf(profile, 'occasion', name) > 20) {
          const phrase = OCCASION_DISPLAY[name] || `zum ${name}`;
          out.push(`Passt <strong>${phrase}</strong>`);
          break;
        }
      }
    }

    // 3. Season match
    const userSeason = this.#answers.season;
    if (userSeason && percentOf(profile, 'season', userSeason) > 20) {
      out.push(`Stark im <strong>${userSeason}</strong>`);
    }

    return out.slice(0, 3);
  }

  /**
   * @param {{ sillage: number|null }} product
   * @returns {{ label: string, percent: number } | null} null when unrated
   */
  #getSillageInfo(product) {
    const raw = sillageOutOf100(product);
    if (!raw) return null;
    return {
      label: getSillageLabel(raw),
      percent: Math.max(10, Math.min(100, raw)),
    };
  }

  #renderResults(results) {
    const { resultsGrid } = this.refs;
    resultsGrid.innerHTML = '';

    results.forEach(({ product }, index) => {
      const rank = RANK_LABELS[index] || { label: 'Match' };
      const { profile } = product;

      // Colours arrive with their leading '#'. The chip sits on white, so the
      // light tone draws the border and the dark tone carries the text.
      const tagsHtml = groupEntries(profile, 'scent')
        .slice(0, 3)
        .map(
          ([name, [, color, colorDark]]) =>
            `<span class="duftquiz__result-tag" style="--tag-border: ${color}; --tag-color: ${colorDark};">${name}</span>`
        )
        .join('');

      const highlights = this.#getMatchHighlights(profile);
      const highlightsHtml = highlights.length
        ? `<ul class="duftquiz__result-highlights">${highlights.map((h) => `<li>${h}</li>`).join('')}</ul>`
        : '';

      const sillage = this.#getSillageInfo(product);
      const sillageHtml = sillage
        ? `<div class="duftquiz__result-sillage">
            <span class="duftquiz__result-sillage-label">Sillage</span>
            <span class="duftquiz__result-sillage-bar"><span class="duftquiz__result-sillage-fill" style="width: ${sillage.percent}%"></span></span>
            <span class="duftquiz__result-sillage-value">${sillage.label}</span>
          </div>`
        : '';

      const card = document.createElement('article');
      card.className = 'duftquiz__result-card';
      card.innerHTML = `
        <a href="${product.url}" class="duftquiz__result-card-media" aria-hidden="true" tabindex="-1">
          <img class="duftquiz__result-card-image" src="${product.image}" alt="${product.title}" loading="lazy" width="240" height="240" />
        </a>
        <div class="duftquiz__result-card-body">
          <div class="duftquiz__result-meta">
            <span class="duftquiz__result-card-vendor">${product.vendor}</span>
            <span class="duftquiz__result-rank">
              <span class="duftquiz__result-rank-label">${rank.label}</span>
            </span>
          </div>
          <h3 class="duftquiz__result-card-title">
            <a href="${product.url}" class="duftquiz__result-card-link">${product.title}</a>
          </h3>
          ${tagsHtml ? `<div class="duftquiz__result-tags">${tagsHtml}</div>` : ''}
          ${highlightsHtml}
          ${sillageHtml}
        </div>
      `;

      resultsGrid.appendChild(card);
    });
  }

  // ── Utilities ──────────────────────────────────────────────

  #showScreen(screen) {
    const { introScreen, quizScreen, loadingScreen, resultsScreen } = this.refs;
    const screens = { intro: introScreen, quiz: quizScreen, loading: loadingScreen, results: resultsScreen };
    for (const [key, el] of Object.entries(screens)) {
      if (el) el.classList.toggle('duftquiz__screen--hidden', key !== screen);
    }
  }
}

if (!customElements.get('duftquiz-component')) {
  customElements.define('duftquiz-component', DuftquizComponent);
}
